import {
  DEFAULT_CLOTH_SETTINGS,
  MAX_CLOTH_GRAVITY,
  MAX_CLOTH_INFLATE,
  MAX_CLOTH_RESOLUTION,
  MAX_CLOTH_STEPS,
  MIN_CLOTH_RESOLUTION,
  MIN_CLOTH_STEPS,
  clampClothSettings,
  defaultClothSettingsFor,
  simulateCushionClothAsync,
} from '../shared/cloth';
import type { BraceEntity, ClothBake, ClothPin, ClothShape, ClothSimSettings } from '../shared/types';
import { ClothPreviewView } from './cloth-preview';
import { subscribeLocale, t } from './i18n';

const PREVIEW_DEBOUNCE_MS = 200;

interface ClothSimDialogOptions {
  onApply: (entityId: string, bake: ClothBake) => void;
  onClose: () => void;
}

/** クリック後のクロスシミュ設定。左に 3D プレビュー、実行中は progress を出し、100% で閉じて反映する。 */
export class ClothSimDialog {
  private readonly root = document.createElement('section');
  private readonly title = document.createElement('h2');
  private readonly subtitle = document.createElement('p');
  private readonly closeHintText = document.createElement('span');
  private readonly canvas = document.createElement('canvas');
  private readonly resolution = this.numberInput(String(DEFAULT_CLOTH_SETTINGS.resolution), '1');
  private readonly steps = this.numberInput(String(DEFAULT_CLOTH_SETTINGS.steps), '10');
  private readonly inflate = this.numberInput(String(DEFAULT_CLOTH_SETTINGS.inflate), '0.05');
  private readonly gravity = this.numberInput(String(DEFAULT_CLOTH_SETTINGS.gravity), '0.05');
  private readonly stiffness = this.numberInput(String(DEFAULT_CLOTH_SETTINGS.stiffness), '0.05');
  private readonly gather = this.numberInput(String(DEFAULT_CLOTH_SETTINGS.gather), '0.05');
  private readonly shape = this.selectInput();
  private readonly pin = this.selectInput();
  private readonly resolutionLabel = document.createElement('span');
  private readonly stepsLabel = document.createElement('span');
  private readonly shapeLabel = document.createElement('span');
  private readonly pinLabel = document.createElement('span');
  private readonly gatherLabel = document.createElement('span');
  private readonly inflateLabel = document.createElement('span');
  private readonly gravityLabel = document.createElement('span');
  private readonly stiffnessLabel = document.createElement('span');
  private readonly shapeField = this.field(this.shapeLabel, this.shape);
  private readonly pinField = this.field(this.pinLabel, this.pin);
  private readonly gatherField = this.field(this.gatherLabel, this.gather);
  private readonly inflateField = this.field(this.inflateLabel, this.inflate);
  private readonly runButton = document.createElement('button');
  private readonly cancelButton = document.createElement('button');
  private readonly progressWrap = document.createElement('div');
  private readonly progress = document.createElement('progress');
  private readonly progressLabel = document.createElement('p');
  private entity: BraceEntity | null = null;
  private abort: AbortController | null = null;
  private previewAbort: AbortController | null = null;
  private previewTimer = 0;
  private preview: ClothPreviewView | null = null;
  private previewBake: ClothBake | null = null;
  private previewSettings: ClothSimSettings | null = null;
  private previewReady = false;
  private running = false;

  constructor(private readonly options: ClothSimDialogOptions) {
    this.root.className = 'grid-editor cloth-sim-dialog';
    this.root.hidden = true;
    this.root.setAttribute('role', 'dialog');
    this.root.setAttribute('aria-modal', 'true');
    this.root.setAttribute('aria-labelledby', 'cloth-sim-title');

    const panel = document.createElement('div');
    panel.className = 'grid-editor-panel';

    const header = document.createElement('header');
    header.className = 'grid-editor-header';
    const titleWrap = document.createElement('div');
    const kicker = document.createElement('p');
    kicker.className = 'grid-editor-kicker';
    kicker.textContent = 'CLOTH';
    this.title.id = 'cloth-sim-title';
    this.subtitle.className = 'grid-editor-subtitle';
    titleWrap.append(kicker, this.title, this.subtitle);
    header.append(titleWrap, this.closeHint());

    this.canvas.setAttribute('aria-label', t('clothSim.preview'));
    const previewWrap = document.createElement('div');
    previewWrap.className = 'cloth-sim-preview';
    previewWrap.append(this.canvas);

    const form = document.createElement('form');
    form.className = 'grid-editor-form';
    form.addEventListener('submit', (event) => {
      event.preventDefault();
      void this.run();
    });
    form.append(
      this.field(this.resolutionLabel, this.resolution),
      this.field(this.stepsLabel, this.steps),
      this.shapeField,
      this.pinField,
      this.gatherField,
      this.inflateField,
      this.field(this.gravityLabel, this.gravity),
      this.field(this.stiffnessLabel, this.stiffness)
    );
    for (const input of [this.resolution, this.steps, this.inflate, this.gravity, this.stiffness, this.gather]) {
      input.addEventListener('input', () => this.onSettingsInput());
    }
    this.shape.addEventListener('change', () => this.onSettingsInput());
    this.pin.addEventListener('change', () => this.onSettingsInput());

    this.progress.max = 1;
    this.progress.value = 0;
    this.progressWrap.className = 'cloth-sim-progress';
    this.progressWrap.hidden = true;
    this.progressLabel.className = 'cloth-sim-progress-label';
    this.progressWrap.append(this.progress, this.progressLabel);

    this.runButton.type = 'button';
    this.runButton.className = 'cloth-sim-run';
    this.runButton.addEventListener('click', () => {
      void this.run();
    });
    this.cancelButton.type = 'button';
    this.cancelButton.className = 'cloth-sim-cancel';
    this.cancelButton.addEventListener('click', () => this.close());

    const actions = document.createElement('div');
    actions.className = 'cloth-sim-actions';
    actions.append(this.cancelButton, this.runButton);

    const side = document.createElement('div');
    side.className = 'cloth-sim-side';
    side.append(form, this.progressWrap, actions);

    const body = document.createElement('div');
    body.className = 'cloth-sim-body';
    body.append(previewWrap, side);

    panel.append(header, body);
    this.root.append(panel);
    document.body.append(this.root);
    this.applyCopy();
    subscribeLocale(() => this.applyCopy());
  }

  get isOpen(): boolean {
    return !this.root.hidden;
  }

  open(entity: BraceEntity): void {
    this.abort?.abort();
    this.abort = null;
    this.abortPreview();
    this.running = false;
    this.entity = entity;
    this.applySettings(entity.geometry.cloth?.settings ?? defaultClothSettingsFor(entity.geometry));
    this.progressWrap.hidden = true;
    this.progress.value = 0;
    this.setBusy(false);
    this.previewBake = entity.geometry.cloth ?? null;
    this.previewSettings = entity.geometry.cloth
      ? clampClothSettings(entity.geometry.cloth.settings)
      : null;
    this.previewReady = this.previewBake !== null && settingsEqual(this.previewSettings, this.readSettings());
    this.root.hidden = false;
    this.applyCopy();
    this.ensurePreview();
    this.preview?.setMesh(this.previewBake);
    if (!this.previewReady) this.schedulePreview(0);
    this.resolution.focus();
  }

  close(): void {
    if (!this.isOpen) return;
    this.abort?.abort();
    this.abort = null;
    this.abortPreview();
    this.disposePreview();
    this.running = false;
    this.entity = null;
    const focused = this.root.querySelector(':focus');
    if (focused instanceof HTMLElement) focused.blur();
    this.root.hidden = true;
    this.options.onClose();
  }

  applyCopy(): void {
    this.title.textContent = t('clothSim.title');
    this.subtitle.textContent = t('clothSim.subtitle');
    this.canvas.setAttribute('aria-label', t('clothSim.preview'));
    this.resolutionLabel.textContent = t('clothSim.resolution');
    this.stepsLabel.textContent = t('clothSim.steps');
    this.shapeLabel.textContent = t('clothSim.shape');
    this.pinLabel.textContent = t('clothSim.pin');
    this.gatherLabel.textContent = t('clothSim.gather');
    this.inflateLabel.textContent = t('clothSim.inflate');
    this.gravityLabel.textContent = t('clothSim.gravity');
    this.stiffnessLabel.textContent = t('clothSim.stiffness');
    this.fillSelect(this.shape, [
      ['volume', t('clothSim.shapeVolume')],
      ['sheet', t('clothSim.shapeSheet')],
    ]);
    this.fillSelect(this.pin, [
      ['none', t('clothSim.pinNone')],
      ['top', t('clothSim.pinTop')],
      ['bottom', t('clothSim.pinBottom')],
      ['left', t('clothSim.pinLeft')],
      ['right', t('clothSim.pinRight')],
      ['ends', t('clothSim.pinEnds')],
    ]);
    this.runButton.textContent = this.running ? t('clothSim.running') : t('clothSim.run');
    this.cancelButton.textContent = t('clothSim.cancel');
    this.closeHintText.textContent = ` ${t('clothSim.closeHint')}`;
    this.syncProgressLabel();
    this.syncOptionalFields();
  }

  private applySettings(settings: ClothSimSettings | undefined): void {
    const next = clampClothSettings(settings);
    this.resolution.min = String(MIN_CLOTH_RESOLUTION);
    this.resolution.max = String(MAX_CLOTH_RESOLUTION);
    this.steps.min = String(MIN_CLOTH_STEPS);
    this.steps.max = String(MAX_CLOTH_STEPS);
    this.inflate.min = '0';
    this.inflate.max = String(MAX_CLOTH_INFLATE);
    this.gravity.min = '0';
    this.gravity.max = String(MAX_CLOTH_GRAVITY);
    this.stiffness.min = '0';
    this.stiffness.max = '1';
    this.gather.min = '0';
    this.gather.max = '1';
    this.resolution.value = String(next.resolution);
    this.steps.value = String(next.steps);
    this.inflate.value = String(next.inflate);
    this.gravity.value = String(next.gravity);
    this.stiffness.value = String(next.stiffness);
    this.gather.value = String(next.gather);
    this.shape.value = next.shape;
    this.pin.value = next.pin;
    this.syncOptionalFields();
  }

  private readSettings(): ClothSimSettings {
    return clampClothSettings({
      resolution: Number(this.resolution.value),
      steps: Number(this.steps.value),
      inflate: Number(this.inflate.value),
      gravity: Number(this.gravity.value),
      stiffness: Number(this.stiffness.value),
      gather: Number(this.gather.value),
      shape: this.shape.value as ClothShape,
      pin: this.pin.value as ClothPin,
    });
  }

  private onSettingsInput(): void {
    if (this.running || !this.isOpen) return;
    this.syncOptionalFields();
    this.previewReady = false;
    this.schedulePreview(PREVIEW_DEBOUNCE_MS);
  }

  private syncOptionalFields(): void {
    this.inflateField.hidden = this.shape.value === 'sheet';
    this.gatherField.hidden = this.pin.value === 'none' || this.pin.value === 'ends';
  }

  private schedulePreview(delayMs: number): void {
    if (this.previewTimer) window.clearTimeout(this.previewTimer);
    this.previewTimer = window.setTimeout(() => {
      this.previewTimer = 0;
      void this.startPreview();
    }, delayMs);
  }

  private async startPreview(): Promise<void> {
    if (this.running || !this.entity || !this.isOpen) return;
    const entity = this.entity;
    const settings = this.readSettings();
    this.applySettings(settings);
    this.previewAbort?.abort();
    const abort = new AbortController();
    this.previewAbort = abort;
    try {
      const bake = await simulateCushionClothAsync(
        entity.geometry,
        settings,
        undefined,
        abort.signal,
        (frame) => {
          if (abort.signal.aborted) return;
          this.preview?.setMesh(frame);
        }
      );
      if (!bake || abort.signal.aborted || this.running || !this.isOpen) return;
      if (!settingsEqual(settings, this.readSettings())) return;
      this.previewBake = bake;
      this.previewSettings = settings;
      this.previewReady = true;
      this.preview?.setMesh(bake);
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      throw error;
    } finally {
      if (this.previewAbort === abort) this.previewAbort = null;
    }
  }

  private async run(): Promise<void> {
    if (this.running || !this.entity) return;
    const entity = this.entity;
    const settings = this.readSettings();
    this.applySettings(settings);
    if (this.previewReady && this.previewBake && settingsEqual(this.previewSettings, settings)) {
      this.applyAndClose(entity.id, this.previewBake);
      return;
    }
    this.abortPreview();
    this.running = true;
    this.abort = new AbortController();
    this.progress.value = 0;
    this.progressWrap.hidden = false;
    this.setBusy(true);
    this.applyCopy();
    try {
      const bake = await simulateCushionClothAsync(
        entity.geometry,
        settings,
        (progress) => {
          this.progress.value = progress;
          this.syncProgressLabel();
        },
        this.abort.signal,
        (frame) => this.preview?.setMesh(frame)
      );
      if (!bake) return;
      this.progress.value = 1;
      this.syncProgressLabel();
      this.applyAndClose(entity.id, bake);
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      throw error;
    } finally {
      if (this.isOpen) {
        this.running = false;
        this.setBusy(false);
        this.applyCopy();
      }
    }
  }

  private applyAndClose(entityId: string, bake: ClothBake): void {
    this.abort = null;
    this.running = false;
    this.entity = null;
    this.abortPreview();
    this.disposePreview();
    const focused = this.root.querySelector(':focus');
    if (focused instanceof HTMLElement) focused.blur();
    this.root.hidden = true;
    this.options.onApply(entityId, bake);
  }

  private abortPreview(): void {
    if (this.previewTimer) {
      window.clearTimeout(this.previewTimer);
      this.previewTimer = 0;
    }
    this.previewAbort?.abort();
    this.previewAbort = null;
  }

  private ensurePreview(): void {
    if (this.preview) return;
    this.preview = new ClothPreviewView(this.canvas);
  }

  private disposePreview(): void {
    this.preview?.dispose();
    this.preview = null;
    this.previewBake = null;
    this.previewSettings = null;
    this.previewReady = false;
  }

  private setBusy(busy: boolean): void {
    this.resolution.disabled = busy;
    this.steps.disabled = busy;
    this.shape.disabled = busy;
    this.pin.disabled = busy;
    this.gather.disabled = busy;
    this.inflate.disabled = busy;
    this.gravity.disabled = busy;
    this.stiffness.disabled = busy;
    this.runButton.disabled = busy;
  }

  private syncProgressLabel(): void {
    const percent = Math.round(this.progress.value * 100);
    this.progressLabel.textContent = t('clothSim.progress', { percent });
  }

  private field(label: HTMLSpanElement, input: HTMLInputElement | HTMLSelectElement): HTMLLabelElement {
    const field = document.createElement('label');
    field.append(label, input);
    return field;
  }

  private closeHint(): HTMLParagraphElement {
    const hint = document.createElement('p');
    hint.className = 'grid-editor-close-hint';
    const kbd = document.createElement('kbd');
    kbd.textContent = 'Esc';
    hint.append(kbd, this.closeHintText);
    return hint;
  }

  private numberInput(value: string, step: string): HTMLInputElement {
    const input = document.createElement('input');
    input.type = 'number';
    input.value = value;
    input.step = step;
    return input;
  }

  private selectInput(): HTMLSelectElement {
    return document.createElement('select');
  }

  private fillSelect(select: HTMLSelectElement, options: [string, string][]): void {
    const current = select.value;
    select.replaceChildren();
    for (const [value, label] of options) {
      const option = document.createElement('option');
      option.value = value;
      option.textContent = label;
      select.append(option);
    }
    if (options.some(([value]) => value === current)) select.value = current;
  }
}

function settingsEqual(a: ClothSimSettings | null, b: ClothSimSettings): boolean {
  if (!a) return false;
  return (
    a.resolution === b.resolution &&
    a.steps === b.steps &&
    a.inflate === b.inflate &&
    a.gravity === b.gravity &&
    a.stiffness === b.stiffness &&
    a.shape === b.shape &&
    a.pin === b.pin &&
    a.gather === b.gather
  );
}
